import React, { useState } from 'react';

import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import {
  ACTION_ACKNOWLEDGE,
  ACTION_RESOLVE,
  issue_action_payload,
  reason_error
} from '../needs_attention/params';
import { NoteField } from './dialog_parts';
import { KIND, error_view } from './error_handlers';
import { DECISIONS_SHOWN, PILL, pill_view, qa_confirm_view } from './adapters';
import QaOthers from '../needs_attention/qa_others';
import { qa_others_text } from '../needs_attention/params';

// an unanswered issue action may or may not have landed; the rows are re-read and a retry is safe.
export const PILL_UNCERTAIN_TEXT =
  'The connection dropped; the issue may or may not have been updated. The list is refreshed; trying again is safe.';

// app_join_text: the session's student app Join tap (1-on-1), shown above the issues as evidence.
const ReviewPill = ({
  issues,
  app_join_text = '',
  teacher_names,
  post_action,
  on_changed,
  on_permission
}) => {
  const view = pill_view(issues, teacher_names);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  // the QA row awaiting its confirmation step (QA cleared leaves the class's other issues open)
  const [confirm_id, setConfirmId] = useState(null);
  const [show_all, setShowAll] = useState(false);

  if (view.state === PILL.HIDDEN) return null;
  // The same reason rule as the Needs attention tab: the endpoint is the same one.
  const reason_err = reason_error(reason);

  const confirming = view.issues.find((i) => i.id === confirm_id && i.can_resolve) || null;
  const confirm = confirming ? qa_confirm_view(confirming, view.issues) : null;

  const act = (issue, action) => {
    setTouched(true);
    if (reason_err || busy) return;
    const qa_clear = action === ACTION_RESOLVE && issue.is_qa;
    setBusy(true);
    setMessage(null);
    post_action(issue_action_payload([issue.id], action, reason))
      .then((data) => {
        const res = ((data && data.results) || []).find(
          (r) => Number(r.issue_id) === Number(issue.id)
        );
        setMessage(
          res && res.applied === false
            ? { tone: 'error', text: res.message || 'This issue could not be updated.' }
            : {
                tone: 'ok',
                text: qa_clear
                  ? qa_confirm_view(issue, view.issues).done_text
                  : `${issue.label}: ${
                      action === ACTION_RESOLVE ? issue.resolved_text : 'acknowledged'
                    }.`
              }
        );
        if (qa_clear) setConfirmId(null);
        // the refetch brings the class's re-raised issues back as their own rows
        on_changed();
      })
      .catch((e) => {
        const err = error_view(e, { write: true });
        if (err.kind === KIND.PERMISSION) {
          on_permission(e);
          return;
        }
        setMessage({
          tone: 'error',
          text: err.kind === KIND.UNCERTAIN ? PILL_UNCERTAIN_TEXT : err.text
        });
        on_changed();
      })
      .finally(() => setBusy(false));
  };

  // an ACKNOWLEDGED row sits under Earlier decisions and can still be resolved
  const actions = (i) =>
    i.can_acknowledge || i.can_resolve ? (
      <span className="tp-actions">
        {i.can_acknowledge ? (
          <button
            type="button"
            className="tp-link"
            onClick={busy ? undefined : () => act(i, ACTION_ACKNOWLEDGE)}
            aria-disabled={busy ? 'true' : undefined}
          >
            Acknowledge
          </button>
        ) : null}
        {i.can_resolve ? (
          <button
            type="button"
            className="tp-link"
            onClick={
              busy ? undefined : () => (i.is_qa ? setConfirmId(i.id) : act(i, ACTION_RESOLVE))
            }
            aria-disabled={busy ? 'true' : undefined}
          >
            {i.resolve_text}
          </button>
        ) : null}
      </span>
    ) : null;

  return (
    <>
      <button
        type="button"
        className="tp-pe-pill"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <StatusPill tone={view.state === PILL.TO_REVIEW ? 'orange' : 'green'}>
          Payment review: {view.text}
        </StatusPill>
      </button>
      {open ? (
        <TpDialog
          title_id="tp-pe-pill-title"
          title="Payment review for this session"
          size="md"
          busy={busy}
          guard_text={discard_guard_text(reason)}
          on_close={() => {
            setOpen(false);
            setConfirmId(null);
          }}
        >
          <div className="tp-modal">
            {app_join_text ? <div className="tp-pe-app-join">{app_join_text}</div> : null}
            {view.open_issues.length ? (
              <ul className="tp-pe-issues">
                {view.open_issues.map((i) => (
                  <li key={i.id}>
                    <div>
                      {i.label} · {i.status_text}
                      {i.detail ? <span className="tp-pe-detail">{i.detail}</span> : null}
                      <QaOthers text={qa_others_text(i.qa_labels)} className="tp-pe-qa-note" />
                    </div>
                    {actions(i)}
                  </li>
                ))}
              </ul>
            ) : null}
            {confirm ? (
              <section className="tp-pe-qa-confirm" aria-labelledby="tp-pe-qa-confirm-title">
                <div id="tp-pe-qa-confirm-title" className="tp-pe-qa-confirm-title">
                  {confirm.title}
                </div>
                <div>This clears only the QA check.</div>
                {confirm.others_text ? (
                  <QaOthers text={confirm.others_text} className="tp-pe-qa-note" />
                ) : (
                  <div className="tp-sub">{confirm.none_text}</div>
                )}
              </section>
            ) : null}
            <NoteField
              id="tp-pe-pill-reason"
              label="Reason (internal, required)"
              value={reason}
              on_change={setReason}
              error_text={touched ? reason_err : ''}
            />
            {message ? (
              <div
                className={`tp-banner ${message.tone === 'ok' ? 'tp-banner-ok' : 'tp-banner-error'}`}
                role={message.tone === 'ok' ? 'status' : 'alert'}
              >
                {message.text}
              </div>
            ) : null}
            <div className="tp-modal-ft">
              {confirm ? (
                <>
                  <button
                    type="button"
                    className="tp-btn-ghost"
                    onClick={busy ? undefined : () => setConfirmId(null)}
                    aria-disabled={busy ? 'true' : undefined}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="tp-btn"
                    onClick={busy ? undefined : () => act(confirming, ACTION_RESOLVE)}
                    aria-disabled={busy ? 'true' : undefined}
                  >
                    {busy ? 'Saving…' : 'QA cleared'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="tp-btn-ghost"
                  onClick={busy ? undefined : () => setOpen(false)}
                  aria-disabled={busy ? 'true' : undefined}
                >
                  Close
                </button>
              )}
            </div>
            {/* read-only history last, so the reason field above never reads as part of it */}
            {view.decisions.length ? (
              <section className="tp-pe-decisions" aria-labelledby="tp-pe-decisions-title">
                <div id="tp-pe-decisions-title" className="tp-pe-decisions-title">
                  Earlier decisions
                </div>
                <ul className="tp-pe-issues">
                  {(show_all || !view.decisions_folded
                    ? view.decisions
                    : view.decisions.slice(0, DECISIONS_SHOWN)
                  ).map((i) => (
                    <li key={i.id}>
                      <div className="tp-pe-decision">
                        <div className="tp-pe-decision-head">
                          <span>{i.label}</span>
                          <StatusPill tone={i.decision.chip_tone}>
                            {i.decision.chip_text}
                          </StatusPill>
                        </div>
                        {i.decision.note ? (
                          <div className="tp-pe-decision-note">{i.decision.note}</div>
                        ) : null}
                        {i.decision.by_text ? (
                          <div className="tp-pe-qa-note">{i.decision.by_text}</div>
                        ) : null}
                      </div>
                      {actions(i)}
                    </li>
                  ))}
                </ul>
                {view.decisions_folded ? (
                  <button type="button" className="tp-link" onClick={() => setShowAll((v) => !v)}>
                    {show_all ? 'Show fewer' : `Show all ${view.decisions.length}`}
                  </button>
                ) : null}
              </section>
            ) : null}
          </div>
        </TpDialog>
      ) : null}
    </>
  );
};

export default ReviewPill;
